/* eslint-disable arrow-body-style */
/* eslint-disable class-methods-use-this */
/* eslint-disable no-param-reassign */
import * as THREE from 'three';
import * as marietta from '../..';
import { SVGSprite } from '../flash/display';
import { WebcadJSONParser } from '../util';
import ItemInfoCollection from '../collection/ItemInfoCollection';
import { AdditionalPinData } from '../manager/AddLidarPinManager';
import WebCADUtil from '../util/WebCADUtil';
import { Point } from '../../core';

/** リサイズハンドル（ドラッグ中の辺・角に応じた固定側＝アンカー） */
export type LidarResizeHandle = 'T' | 'B' | 'L' | 'R' | 'TL' | 'TR' | 'BL' | 'BR';

/** 2D表示領域（スクロール内側）の要素ID */
const VIEW_INNER_ELEMENT_ID: string = 'webcad2dViewInner';

export default class TemporaryGroupItemForLidarAddPin {
  import_info: AdditionalPinData;

  floor: number;

  tarLayer: marietta.webcad.layer.FloorShitaeLayer;

  roomList: ItemInfoCollection[];

  pinList: ItemInfoCollection[];

  markList: ItemInfoCollection[];

  convertPoint: THREE.Vector2;

  public spt: SVGSprite;

  /**
   * 互換用。既存参照があっても壊れないように残す
   * 実体は「全部屋グループ」を指す
   */
  public roomSpt: SVGGElement;

  /**
   * 部屋全体のBBox取得用
   */
  public roomsSpt: SVGGElement;

  public cancelCallBack: Function;

  private _virtualScaleX = 1;
  private _virtualScaleY = 1;

  private _pinSprites: SVGSprite[] = [];
  private _markSprites: SVGSprite[] = [];

  private _contentG: SVGGElement;
  private _roomsG: SVGGElement;
  private _pinsG: SVGGElement;
  private _marksG: SVGGElement;

  private _contentTx = 0;
  private _contentTy = 0;

  private _rooms: Array<{
    data: ItemInfoCollection;
    group: SVGGElement;
    path: SVGPathElement;
    title: SVGTextElement;
    localDots: Array<{ x: number; y: number }>;
    titlePos: { x: number; y: number };
    titleWidth: number;
  }> = [];

  constructor(info: AdditionalPinData) {
    this.import_info = info;

    // 部屋、ピン、画像検知マークを取り出す
    this.roomList = WebcadJSONParser.extractRoomInfoList(info.planData.plan.room).filter((item) => item.floor === 1);
    this.pinList = WebcadJSONParser.extractFreeTextItem(info.planData.plan.text).filter((item) => item.floor === 1);
    this.markList = WebcadJSONParser.extractMarkItem(info.planData.plan.mark).filter((item) => item.floor === 1);

    const viewObj = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    this.spt = new SVGSprite(viewObj);

    // 中身用グループ
    this._contentG = document.createElementNS('http://www.w3.org/2000/svg', 'g') as SVGGElement;
    this.spt.element.appendChild(this._contentG);

    // 種別別グループ
    this._roomsG = document.createElementNS('http://www.w3.org/2000/svg', 'g') as SVGGElement;
    this._pinsG = document.createElementNS('http://www.w3.org/2000/svg', 'g') as SVGGElement;
    this._marksG = document.createElementNS('http://www.w3.org/2000/svg', 'g') as SVGGElement;

    this._contentG.appendChild(this._roomsG);
    this._contentG.appendChild(this._pinsG);
    this._contentG.appendChild(this._marksG);

    this.roomsSpt = this._roomsG;
    this.roomSpt = this._roomsG;

    // 部屋、建具、ピンを読み込んで位置合わせダイアログ表示
    this.createImage();

    this.spt.x = (this.convertPoint.x * -1);
    this.spt.y = (this.convertPoint.y * -1);
    this.spt.update();

    this.addMoveEvent();
  }

  /**
   * オーバーレイを現在の2D表示領域の中心へ配置する。
   * 取り込みデータの絶対座標のままだと画面外に描画され、ピン・コントローラーが
   * 見えず操作できない（元図面が半透明のまま固まる）ため、addParts後に呼ぶ。
   * 表示中心のスクリーン座標を親レイヤーのローカル座標へ逆変換して配置する
   * （ステージの拡大率・スクロール位置に依存しない）。
   */
  public centerInView(): void {
    if (!this.spt || !this.spt.element) return;

    const viewEl = document.getElementById(VIEW_INNER_ELEMENT_ID);
    const parentEl = this.spt.element.parentNode as SVGGraphicsElement | null;
    if (!viewEl || !parentEl || !parentEl.getScreenCTM) return;

    const ctm = parentEl.getScreenCTM();
    if (!ctm) return;

    const rect = viewEl.getBoundingClientRect();
    const screenCenter = new DOMPoint(rect.left + rect.width / 2, rect.top + rect.height / 2);
    const local = screenCenter.matrixTransform(ctm.inverse());

    this.spt.x = local.x;
    this.spt.y = local.y;
    this.spt.update();
  }

  private addMoveEvent() {
    // iPad(タブレット)・スマホはタッチ、PCはマウスで同じドラッグ移動を実現する。
    // 拡縮・回転ハンドル（ControlPicker/ControlRotator）と同じ isMobile || isTablet 分岐方式に揃える。
    const ua = marietta.webcad.WebCADGlobal.userAgentManager;
    const isTouchDevice: boolean = ua.isMobile || ua.isTablet;

    let isDragging = false;
    let startX = 0;
    let startY = 0;

    const onMove = (e: MouseEvent | TouchEvent) => {
      if (!isDragging) return;

      // 2本指以上はピンチ/パン操作とみなし、移動ドラッグを中断（衝突させない）
      const touchEvent = e as TouchEvent;
      if (touchEvent.touches && touchEvent.touches.length >= WebCADUtil.MULTI_TOUCH_FINGER_COUNT) {
        onEnd();
        return;
      }

      // タッチ時はブラウザのスクロール/ピンチを抑止
      if (isTouchDevice) e.preventDefault();

      const stage = marietta.webcad.WebCADGlobal.layerGroup?.spt;
      const sx = stage?.scaleX ?? 1;
      const sy = stage?.scaleY ?? 1;

      const { x: clientX, y: clientY } = WebCADUtil.getEventClientPoint(e);

      // 画面(px)の差分 → ステージ座標の差分へ変換
      const dx = (clientX - startX) / sx;
      const dy = (clientY - startY) / sy;

      this.move(this.spt.x + dx, this.spt.y + dy);
      marietta.webcad.WebCADGlobal.itemManager.itemController?.resizeToTarget();

      startX = clientX;
      startY = clientY;
    };

    const onEnd = () => {
      isDragging = false;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onEnd);
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onEnd);
    };

    const onStart = (e: MouseEvent | TouchEvent) => {
      // キャンバスのパン（LayerGroup の con の touchstart/mousedown）へ伝播させない
      e.stopPropagation();
      if (isTouchDevice) e.preventDefault();

      isDragging = true;
      const { x: clientX, y: clientY } = WebCADUtil.getEventClientPoint(e);
      startX = clientX;
      startY = clientY;

      if (isTouchDevice) {
        document.addEventListener('touchmove', onMove, { passive: false });
        document.addEventListener('touchend', onEnd);
      } else {
        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onEnd);
      }
    };

    if (isTouchDevice) {
      this.spt.element.addEventListener('touchstart', onStart, { passive: false });
    } else {
      this.spt.element.addEventListener('mousedown', onStart);
    }
  }

  /**
   * 初期基準は「部屋のみ」。
   * ピン・マークは中心計算の基準に含めない。
   */
  private boundsRect(): { minX: number, maxX: number, minY: number, maxY: number } {
    let minX: number = NaN;
    let maxX: number = NaN;
    let minY: number = NaN;
    let maxY: number = NaN;

    for (let i: number = 0; i < this.roomList.length; i += 1) {
      for (let k: number = 0; k < this.roomList[i].dotList.length; k += 1) {
        const dot = this.roomList[i].dotList[k];
        if (Number.isNaN(maxX) || maxX < dot.x) maxX = dot.x;
        if (Number.isNaN(maxY) || maxY < dot.y) maxY = dot.y;
        if (Number.isNaN(minX) || dot.x < minX) minX = dot.x;
        if (Number.isNaN(minY) || dot.y < minY) minY = dot.y;
      }
    }

    if (Number.isNaN(minX)) minX = 0;
    if (Number.isNaN(minY)) minY = 0;
    if (Number.isNaN(maxX)) maxX = 0;
    if (Number.isNaN(maxY)) maxY = 0;

    return { minX, maxX, minY, maxY };
  }

  createImage() {
    const { minX, maxX, minY, maxY } = this.boundsRect();

    this.convertPoint = new THREE.Vector2(
      -(minX + (maxX - minX) / 2),
      -(minY + (maxY - minY) / 2)
    );

    this._rooms = [];
    this._pinSprites = [];
    this._markSprites = [];
    this._virtualScaleX = 1;
    this._virtualScaleY = 1;

    // クリア
    this._roomsG.innerHTML = '';
    this._pinsG.innerHTML = '';
    this._marksG.innerHTML = '';

    // 部屋
    for (let i = 0; i < this.roomList.length; i += 1) {
      const data = this.roomList[i];
      this.createRoomView(data, this.convertPoint);
    }

    // ピン：サイズ固定、位置だけ管理するため sprite を保持
    for (let i = 0; i < this.pinList.length; i += 1) {
      const data = this.pinList[i];
      this.createPinView(data, this.convertPoint);
    }

    // マーク：サイズ固定、位置だけ管理するため sprite を保持
    for (let i = 0; i < this.markList.length; i += 1) {
      const data = this.markList[i];
      this.createMarkView(data, this.convertPoint);
    }

    let viewObj: Element;
    if (!this.spt) {
      viewObj = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    } else {
      viewObj = this.spt.element;
    }

    if (!this.spt) {
      this.spt = new SVGSprite(viewObj);
      this.spt.update();
    }
  }

  /**
   * 部屋ビューの作成
   */
  private createRoomView(data: ItemInfoCollection, convertPoint: THREE.Vector2): void {
    const _defaultRoomSetting = marietta.webcad.WebCADGlobal.getRoomConfig(data.id);

    const group = document.createElementNS('http://www.w3.org/2000/svg', 'g') as SVGGElement;
    const room = document.createElementNS('http://www.w3.org/2000/svg', 'path') as SVGPathElement;

    // ローカル点群（convertPoint 足したあと）を保持
    const localDots = data.dotList.map((p) => ({
      x: p.x + convertPoint.x,
      y: p.y + convertPoint.y,
    }));

    room.setAttribute('d', this.buildPathDFromLocalDots(localDots));
    room.setAttribute('fill', _defaultRoomSetting.color);
    room.setAttribute('fill-opacity', '0.5');
    room.setAttribute('stroke', '#666666');
    room.setAttribute('stroke-width', '10');
    group.appendChild(room);

    // 部屋名（フォント固定で、位置だけ動かす）
    const point: Point = WebCADUtil.getPolygonCentroid(data.dotList);
    const titleObj = document.createElementNS('http://www.w3.org/2000/svg', 'text') as SVGTextElement;
    titleObj.setAttribute('fill', '#000000');
    titleObj.textContent = data.text;

    const textMatrix: TextMetrics = WebCADUtil.calculateTextMetrics(data.text, '16px Yu Gothic');
    titleObj.setAttribute('class', 'roomText_title');
    titleObj.setAttribute('font-size', `${marietta.webcad.WebCADGlobal.roomtextFontSize.toString()}px`);

    // タイトルのローカル座標（convertPoint 足したあと）
    const titleLocalX = (point.x + convertPoint.x) - (textMatrix.width + 10) / 2;
    const titleLocalY = (point.y + convertPoint.y);

    titleObj.setAttribute(
      'style',
      `text-align:center; width:${textMatrix.width + 10}px; transform:translate(${titleLocalX}px,${titleLocalY}px)`
    );

    group.appendChild(titleObj);
    this._roomsG.appendChild(group);

    this._rooms.push({
      data,
      group,
      path: room,
      title: titleObj,
      localDots,
      titlePos: { x: titleLocalX, y: titleLocalY },
      titleWidth: textMatrix.width + 10,
    });
  }

  /**
   * ピンの作成
   */
  private createPinView(data: ItemInfoCollection, convertPoint: THREE.Vector2): void {
    const viewObj = document.querySelector('#lib_control_pinpicture svg > g')!.cloneNode(true) as SVGGElement;

    const spt = new SVGSprite(viewObj);
    spt.x = data.x + convertPoint.x;
    spt.y = data.y + convertPoint.y;
    spt.rotation = data.rotation;
    spt.update();

    this._pinsG.appendChild(viewObj);
    this._pinSprites.push(spt);
  }

  /**
   * マークの作成
   */
  private createMarkView(data: ItemInfoCollection, convertPoint: THREE.Vector2): void {
    const viewObj = document.createElementNS('http://www.w3.org/2000/svg', 'path') as SVGPathElement;
    viewObj.setAttribute('d', 'M -15 -15 L 0 15 L 15 -15 Z');
    viewObj.setAttribute('fill', '#5B9BD5');

    const spt = new SVGSprite(viewObj);
    spt.x = data.x + convertPoint.x;
    spt.y = data.y + convertPoint.y;
    spt.rotation = data.rotation;
    spt.update();

    this._marksG.appendChild(viewObj);
    this._markSprites.push(spt);
  }

  /**
   * measureRoomsFrameForController と同じ content ローカル座標の点を図面座標へ（回転 atan2 の軸用）
   */
  public contentLocalToPlanPoint(localX: number, localY: number): Point {
    const p = this.localToPlan(localX, localY);
    return new Point(p.x, p.y);
  }

  /**
   * contentローカル座標 → 図面座標（親sptの回転込み）
   */
  private localToPlan(localX: number, localY: number): { x: number; y: number } {
    const preX = this.spt.x + localX;
    const preY = this.spt.y + localY;

    const theta = (this.spt.rotation * Math.PI) / 180;
    const sin = Math.sin(theta);
    const cos = Math.cos(theta);

    const pivotX = this.spt.x;
    const pivotY = this.spt.y;

    const dx = preX - pivotX;
    const dy = preY - pivotY;

    return {
      x: pivotX + cos * dx - sin * dy,
      y: pivotY + sin * dx + cos * dy,
    };
  }

  private getPointInSptLocal(targetEl: SVGGraphicsElement): { x: number; y: number } {
    const sptEl = this.spt.element as unknown as SVGGraphicsElement;

    const targetCTM = targetEl.getScreenCTM();
    const sptCTM = sptEl.getScreenCTM();
    if (!targetCTM || !sptCTM) return { x: 0, y: 0 };

    const p = new DOMPoint(0, 0).matrixTransform(targetCTM);
    const pLocal = p.matrixTransform(sptCTM.inverse());

    return { x: pLocal.x, y: pLocal.y };
  }

  /**
   * ピンを図面へ反映
   */
  public insertPinsToPlan(): marietta.webcad.item.PinPicture[] {
    return this.pinList.map((pin, i) => {
      const sptPin = this._pinSprites[i];

      const pinEl = sptPin.element as unknown as SVGGraphicsElement;
      const { x: localX, y: localY } = this.getPointInSptLocal(pinEl);

      const { x, y } = this.localToPlan(localX, localY);
      pin.x = x;
      pin.y = y;
      pin.rotation += this.spt.rotation;

      // 画像バイナリ保持
      const links = pin.link;
      const sources = links.map((link) => {
        return (this.import_info.imgBinary.find((item) => item.name.indexOf(link.name) > -1))!.src;
      });

      for (let j = 0; j < sources.length; j += 1) {
        const source = sources[j];
        const linkList = pin.link;

        if (!linkList[j]) {
          linkList.push({ url: '', id: '', name: '', binary: source });
        } else {
          linkList[j].binary = source;
        }
      }

      pin.is_register_img = true;

      if (pin.transformMatrix) {
        this.setTransformMatrix(pin, pin.rotation);
      }

      pin.floor = marietta.webcad.WebCADGlobal.webcad2Dmanager.currentFloor;

      return marietta.webcad.WebCADGlobal.itemManager.createItem(pin) as marietta.webcad.item.PinPicture;
    });
  }

  /**
   * 画像検知ピン図面へ反映
   */
  public insertMarksToPlan(): marietta.webcad.item.Mark[] {
    return this.markList.map((mark, i) => {
      const sptMark = this._markSprites[i];
      const markEl = (sptMark as any).element as SVGGraphicsElement;

      const { x: localX, y: localY } = this.getPointInSptLocal(markEl);
      const { x, y } = this.localToPlan(localX, localY);
      mark.x = x;
      mark.y = y;

      mark.rotation += this.spt.rotation;

      if (mark.transformMatrix) {
        this.setTransformMatrix(mark, mark.rotation);
      }

      mark.floor = marietta.webcad.WebCADGlobal.webcad2Dmanager.currentFloor;

      return marietta.webcad.WebCADGlobal.itemManager.createItem(mark) as marietta.webcad.item.Mark;
    });
  }

  private setTransformMatrix(itemInfo: ItemInfoCollection, angle: number) {
    const convertAngle = (inputAngle: number): number => {
      return (inputAngle <= 180) ? 180 - inputAngle : 540 - inputAngle;
    };
    const matrix = new THREE.Matrix4();
    matrix.fromArray(itemInfo.transformMatrix);

    const position = new THREE.Vector3();
    const quaternion = new THREE.Quaternion();
    const scale = new THREE.Vector3();
    matrix.decompose(position, quaternion, scale);

    const euler = new THREE.Euler().setFromQuaternion(quaternion, 'YXZ');
    euler.y = THREE.MathUtils.degToRad(convertAngle(angle % 360));
    quaternion.setFromEuler(euler);

    matrix.compose(position, quaternion, scale);

    itemInfo.transformMatrix = matrix.toArray();
  }

  get viewObj(): Element {
    return this.spt.element;
  }

  public deleteMe(): void {
    if (this.spt?.onEnterFrame) {
      delete this.spt.onEnterFrame;
    }

    if (this.spt?.parent != null) {
      this.spt.parent.removeChild(this.spt);
    }
    this.spt = null;
  }

  /**
   * 回転
   */
  rotate(angle: number): void {
    if (angle >= 360) angle %= 360;
    this.spt.rotation = angle;
    this.spt.update();
  }

  /**
   * 移動
   */
  move(x: number, y: number) {
    this.moveX(x);
    this.moveY(y);
  }

  /**
   * 移動X
   */
  moveX(x: number) {
    this.spt.x = x;
    this.spt.update();
  }

  /**
   * 移動Y
   */
  moveY(y: number) {
    this.spt.y = y;
    this.spt.update();
  }

  private buildPathDFromLocalDots(dots: Array<{ x: number; y: number }>): string {
    if (!dots || dots.length === 0) return '';
    const head = dots[0];
    let d = `M ${head.x} ${head.y}`;
    for (let i = 1; i < dots.length; i += 1) {
      d += ` L ${dots[i].x} ${dots[i].y}`;
    }
    return `${d} Z`;
  }

  /**
   * 回転を一時的に0にして、部屋全体の現在表示BBoxを取得（px）
   */
  private getRoomsRectIgnoringRotation(): { rect: DOMRect; motoRot: number } {
    const motoRot: number = this.spt.rotation;
    this.rotate(0);
    const rect = this.roomsSpt.getBoundingClientRect();
    this.rotate(motoRot);
    return { rect, motoRot };
  }

  /**
   * ItemController 用。ピン・マークを除いた部屋（_roomsG）の枠。
   * changeSize と同様に部屋のみを基準にする。
   * 座標は target.spt のルート <g> 直下のローカル（回転前）。
   */
  public measureRoomsFrameForController(): { cx: number; cy: number; width: number; height: number } {
    const motoRot: number = this.spt.rotation;
    this.rotate(0);
    try {
      const b = this._roomsG.getBBox();
      const w = b.width;
      const h = b.height;
      if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) {
        const rect = this.roomsSpt.getBoundingClientRect();
        const width = rect.width / marietta.webcad.WebCADGlobal.scaleSize;
        const height = rect.height / marietta.webcad.WebCADGlobal.scaleSize;
        return { cx: 0, cy: 0, width, height };
      }
      const cx = this._contentTx + b.x + w / 2;
      const cy = this._contentTy + b.y + h / 2;
      return { cx, cy, width: w, height: h };
    } finally {
      this.rotate(motoRot);
    }
  }

  /**
   * 現在の部屋サイズ（curW/curH）を取得
   * ピン・マークは基準に含めない
   */
  private getCurrentRoomSize(): { curW: number; curH: number; beforeRect: DOMRect } {
    const { rect: beforeRect } = this.getRoomsRectIgnoringRotation();

    const curW = beforeRect.width / marietta.webcad.WebCADGlobal.scaleSize;
    const curH = beforeRect.height / marietta.webcad.WebCADGlobal.scaleSize;

    return { curW, curH, beforeRect };
  }

  /**
   * 部屋点群からローカルBBox（content座標）
   */
  private getRoomLocalBoundsFromDots(): { minX: number; maxX: number; minY: number; maxY: number } | null {
    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;
    for (const r of this._rooms) {
      for (let i = 0; i < r.localDots.length; i += 1) {
        const d = r.localDots[i];
        if (d.x < minX) minX = d.x;
        if (d.x > maxX) maxX = d.x;
        if (d.y < minY) minY = d.y;
        if (d.y > maxY) maxY = d.y;
      }
    }
    if (!Number.isFinite(minX) || !Number.isFinite(maxX)) return null;
    return { minX, maxX, minY, maxY };
  }

  /**
   * ハンドルに対応するスケール基点（反対側の辺・角を固定）
   * SVGは y 下向きが正（上が minY、下が maxY）
   */
  private anchorForResizeHandle(
    handle: LidarResizeHandle,
    b: { minX: number; maxX: number; minY: number; maxY: number },
  ): { ax: number; ay: number; lockX: boolean; lockY: boolean } {
    switch (handle) {
      case 'L':
        return { ax: b.maxX, ay: (b.minY + b.maxY) / 2, lockX: false, lockY: true };
      case 'R':
        return { ax: b.minX, ay: (b.minY + b.maxY) / 2, lockX: false, lockY: true };
      case 'T':
        return { ax: (b.minX + b.maxX) / 2, ay: b.maxY, lockX: true, lockY: false };
      case 'B':
        return { ax: (b.minX + b.maxX) / 2, ay: b.minY, lockX: true, lockY: false };
      case 'TL':
        return { ax: b.maxX, ay: b.maxY, lockX: false, lockY: false };
      case 'TR':
        return { ax: b.minX, ay: b.maxY, lockX: false, lockY: false };
      case 'BL':
        return { ax: b.maxX, ay: b.minY, lockX: false, lockY: false };
      case 'BR':
      default:
        return { ax: b.minX, ay: b.minY, lockX: false, lockY: false };
    }
  }

  /**
   * pin：位置だけ拡縮（見た目サイズは固定）
   */
  private applyResizeToPins(sx: number, sy: number, ax: number | null, ay: number | null): void {
    for (const p of this._pinSprites) {
      if (ax !== null && ay !== null) {
        p.x = ax + (p.x - ax) * sx;
        p.y = ay + (p.y - ay) * sy;
      } else {
        p.x *= sx;
        p.y *= sy;
      }
      p.update();
    }
  }

  /**
   * mark：位置だけ拡縮（見た目サイズは固定）
   */
  private applyResizeToMarks(sx: number, sy: number, ax: number | null, ay: number | null): void {
    for (const m of this._markSprites) {
      if (ax !== null && ay !== null) {
        m.x = ax + (m.x - ax) * sx;
        m.y = ay + (m.y - ay) * sy;
      } else {
        m.x *= sx;
        m.y *= sy;
      }
      m.update();
    }
  }

  /**
   * room：点群を拡縮してpath更新、テキスト位置も拡縮
   */
  private applyResizeToRooms(sx: number, sy: number, ax: number | null, ay: number | null): void {
    for (const r of this._rooms) {
      for (let i = 0; i < r.localDots.length; i += 1) {
        if (ax !== null && ay !== null) {
          r.localDots[i].x = ax + (r.localDots[i].x - ax) * sx;
          r.localDots[i].y = ay + (r.localDots[i].y - ay) * sy;
        } else {
          r.localDots[i].x *= sx;
          r.localDots[i].y *= sy;
        }
      }
      r.path.setAttribute('d', this.buildPathDFromLocalDots(r.localDots));

      if (ax !== null && ay !== null) {
        r.titlePos.x = ax + (r.titlePos.x - ax) * sx;
        r.titlePos.y = ay + (r.titlePos.y - ay) * sy;
      } else {
        r.titlePos.x *= sx;
        r.titlePos.y *= sy;
      }
      r.title.setAttribute(
        'style',
        `text-align:center; width:${r.titleWidth}px; transform:translate(${r.titlePos.x}px,${r.titlePos.y}px)`
      );
    }
  }

  /**
   * 画面上(px)の中心が変わらないように親spt.x/yを補正
   * 基準は「部屋全体のみ」
   */
  private keepRoomScreenCenter(beforeRect: DOMRect): void {
    const beforeCenter = {
      cx: beforeRect.left + beforeRect.width / 2,
      cy: beforeRect.top + beforeRect.height / 2,
    };

    const { rect } = this.getRoomsRectIgnoringRotation();
    const afterCenter = {
      cx: rect.left + rect.width / 2,
      cy: rect.top + rect.height / 2,
    };

    const deltaPxX = beforeCenter.cx - afterCenter.cx;
    const deltaPxY = beforeCenter.cy - afterCenter.cy;

    const stage = marietta.webcad.WebCADGlobal.layerGroup?.spt;
    const stageScaleX = stage?.scaleX ?? 1;
    const stageScaleY = stage?.scaleY ?? 1;

    const dx = deltaPxX / stageScaleX;
    const dy = deltaPxY / stageScaleY;

    this.spt.x += dx;
    this.spt.y += dy;
    this.spt.update();
  }

  /**
   * contentの中心を原点へ寄せる。
   * 基準は部屋のみ。
   * ピン・マークは同じcontent配下なので相対位置のまま追随する。
   */
  private recenterContentToOrigin(): void {
    if (!this._contentG || !this._roomsG) return;

    const b = this._roomsG.getBBox();
    const cx = b.x + b.width / 2;
    const cy = b.y + b.height / 2;

    if (Math.abs(cx) < 1e-6 && Math.abs(cy) < 1e-6) return;

    this._contentTx += -cx;
    this._contentTy += -cy;

    const nextTx = this._contentTx;
    const nextTy = this._contentTy;
    this._contentG.setAttribute('transform', `translate(${nextTx},${nextTy})`);

    // 絶対位置維持のため親を逆方向に補正
    this.spt.x += cx;
    this.spt.y += cy;
  }

  /**
   * サイズ変更
   * - 基準は「部屋のみ」
   * - 親(this.spt)はscaleしない（pin/markの見た目サイズを維持するため）
   * - リサイズ率(sx/sy)で、pin/markの位置だけ更新
   * - roomはdotList(ローカル点群)を更新してpathを描き直す
   * @param resizeHandle 指定時はそのハンドルの反対側を固定して拡縮（辺ドラッグは片軸のみ）
   *  https://mari.backlog.jp/view/STARTS_LIDAR-2017#comment-1538273672
   */
  public changeSize(w: number, h: number, keepCenter: boolean = true, resizeHandle?: LidarResizeHandle): void {
    if (!this.spt || !this.spt.element) return;
    if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) return;

    // 現在の部屋表示サイズを取得
    const { curW, curH, beforeRect } = this.getCurrentRoomSize();
    if (!Number.isFinite(curW) || !Number.isFinite(curH) || curW <= 0 || curH <= 0) return;

    let sx = w / curW;
    let sy = h / curH;

    const bounds = this.getRoomLocalBoundsFromDots();
    let ax: number | null = null;
    let ay: number | null = null;

    if (resizeHandle && bounds) {
      const anchor = this.anchorForResizeHandle(resizeHandle, bounds);
      ax = anchor.ax;
      ay = anchor.ay;
      if (anchor.lockY) sy = 1;
      if (anchor.lockX) sx = 1;
    }

    this._virtualScaleX *= sx;
    this._virtualScaleY *= sy;

    // 各要素へ反映
    // roomは基準そのもの
    this.applyResizeToRooms(sx, sy, ax, ay);
    // pin/markは部屋に付随して位置のみ倍率補正
    // https://mari.backlog.jp/view/STARTS_LIDAR-2017#comment-1538273672
    this.applyResizeToPins(sx, sy, ax, ay);
    this.applyResizeToMarks(sx, sy, ax, ay);

    this.spt.update();

    const doKeepCenter = resizeHandle ? false : keepCenter;
    if (doKeepCenter) {
      this.keepRoomScreenCenter(beforeRect);
    }

    if (!resizeHandle) {
      this.recenterContentToOrigin();
    }

    this.spt.update();
  }
}
